import { Injectable } from '@angular/core';
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';
import {CommonService} from './common.service';
import {MessageService} from './message.service';
import firebase from 'firebase';
import {COLLECTION_NAME, DEFAULT_JISSHIDATE, QUERY_LIMIT_ALERT} from '../../shared/constants';
import firestore = firebase.firestore;
import {firstValueFrom, Observable} from 'rxjs';
import cloneDeep from 'lodash-es/cloneDeep';
import {MatDialog} from '@angular/material/dialog';
import {AlertEditDialogComponent} from '../../shared/dialog/alert-edit-dialog/alert-edit-dialog.component';
import {HomeService} from './home.service';
import {Alert} from '../../shared/interfaces/alert';
import {Keisha} from '../../shared/interfaces/keisha';

export interface AlertKensakuKeys {
  farmId?: string,
  keishaId?: string,
  kakuninDate?: string,
  fromDate?: string,
  toDate?: string,
}

@Injectable({
  providedIn: 'root'
})
export class AlertService {
  alertObservable = new Observable<Alert[]>();
  kakuninZumiAlertObservable = new Observable<Alert[]>();
  onlyShowMikakunin = true;
  constructor(private db: AngularFirestore,
              private homeService: HomeService,
              private sessionService: SessionService,
              private commonService: CommonService,
              private messageService: MessageService,
              public dialog: MatDialog,) { }

  /**
   *
   */
  genAlertRef() : firebase.firestore.CollectionReference<firebase.firestore.DocumentData> {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.firestore.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId).collection(COLLECTION_NAME.ALERT);
  }

  /**
   *
   * @param kensakuKey
   */
  getAlertObservable(kensakuKey: AlertKensakuKeys): Observable<Alert[]> {
    return this.genAlertQuery(kensakuKey).valueChanges({idField: 'id'}) as Observable<Alert[]>;
  }

  genAlertQuery(kensakuKey: AlertKensakuKeys) {
    console.table(kensakuKey);
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.ALERT, ref => {
          let query: firestore.CollectionReference |
            firestore.Query = ref;
          // 無闇に複合インデックスを作成したくないので farmId か keishaId のどちらかしか指定されないようにしておく
          if (kensakuKey?.farmId) {
            query = query.where('farmId', '==', kensakuKey.farmId);
          } else if (kensakuKey?.keishaId) {
            query = query.where('keishaId', '==', kensakuKey.keishaId);
          }
          if (kensakuKey?.fromDate) {
            // if (typeof kensakuKey.fromDate === 'string') {
            //   kensakuKey.fromDate = this.commonService.convertDateStringToObject(kensakuKey.fromDate);
            // }
            query = query.where('date', '>=', this.commonService.formatDateRangePickerDate(kensakuKey.fromDate));
          }
          if (kensakuKey?.toDate) {
            // if (typeof kensakuKey.toDate === 'string') {
            //   kensakuKey.toDate = this.commonService.convertDateStringToObject(kensakuKey.toDate);
            // }
            query = query.where('date', '<=', this.commonService.formatDateRangePickerDate(kensakuKey.toDate));
          }
          if (typeof kensakuKey?.kakuninDate !== 'undefined'
            && kensakuKey?.kakuninDate !== null) {
            query = query.where('kakuninDate', '==', kensakuKey.kakuninDate);
          } else {
            // kakuninDate での絞り込みを行っていない場合は、取得件数を制限する
            query = query.limit(QUERY_LIMIT_ALERT);
            console.log('query limit');
          }
          query = query.orderBy('date', 'desc');
          return query;
        }
      )
  }

  /**
   *
   */
  getKakuninZumiAlert() {
    if (!this.kakuninZumiAlertObservable && this.homeService.selectedFarmId) {
      this.kakuninZumiAlertObservable = this.getAlertObservable(
        {farmId: this.homeService.selectedFarmId, kakuninDate:
            this.commonService.getDateWithString()});
    }
  }

  /**
   *
   * @param alert
   */
  updateAlert(alert: any) {
    return new Promise<void>((resolve, reject) =>{
      const ref = this.genAlertRef().doc(alert.id);
      ref.set(alert).then(() => {
        this.messageService.pushInfo('アラートを確認済みにしました');
        resolve();
      }).catch(e => {
        console.log(e);
        this.messageService.pushError('データの更新に失敗しました');
        reject();
      });
    });
  }

  /**
   *
   * @param alert
   */
  alertRowClicked(alert: Alert) {
    const dialogRef = this.dialog.open<AlertEditDialogComponent, Alert>(AlertEditDialogComponent, {
      // data: {子コンポーネントに値を渡すことができる},
      data: cloneDeep(alert),
      maxWidth: '50vw',
      minWidth: '50vw',
      height: '50vh',
      panelClass: 'dialog-with-close', // dialog外クリックでdialogを閉じる。
    });

    return dialogRef.afterClosed().subscribe(result => {
      if (!result) { return; }
    });
  }

  /**
   *
   */
  selectedFarmChanged() {
    if (!this.homeService.selectedFarmId) { return; }
    this.alertObservable
      = this.getAlertObservable({farmId: this.homeService.selectedFarmId, kakuninDate: ''});
    this.kakuninZumiAlertObservable = this.getAlertObservable(
      {farmId: this.homeService.selectedFarmId, kakuninDate:
          this.commonService.getDateWithString()});
  }

  /**
   * アラートの日時を返す。直近の日付については「今日」や「昨日」などに置き換えられる。。
   * @param alert
   */
  alertDate(alert: Alert) {
    return this.commonService.replaceRecentDateInWord(this.alertDateInStandardDate(alert));
  }

  /**
   *
   * @param alert
   */
  private alertDateInStandardDate(alert: Alert): string {
    const date = alert.timeStamp.toDate();
    return this.commonService.getDateWithString(date);
  }

  /**
   * alertAbstractは「アラート種別 : 問題の値」というフォーマットで成り立っている
   * titleOrStatusにて title を指定すると アラート種別部分
   * titleOrStatusにて status を指定すると 問題の値部分
   * を切り出して返す
   * @param alert
   * @param titleOrStatus 取得したい部分を指定
   */
  extractTitleOrStatus(alert: Alert, titleOrStatus: 'title' | 'status') {
    const abstract = alert.alertAbstract;
    if (typeof abstract === 'undefined') {
      return '';
    }
    const separaters = [' : ', ' :', ': ', '：']; // 半角スペース＋コロン＋半角スペース、半角スペース＋コロン、コロン＋半角スペース、全角コロン
    const indexOfUsedDeliminater = separaters
      .map(elm => abstract.indexOf(elm) >= 0)
      .findIndex((bool: boolean) => bool);
    if (indexOfUsedDeliminater < 0) {return '';}
    const separator = separaters[indexOfUsedDeliminater];

    const title = abstract.split(separator)[0];
    const status = abstract.split(separator).slice(-1)[0];
    return titleOrStatus === 'title' ? title : status;
  }

  /**
   * 削除済みの全鶏舎のアラートを確認済みにする
   * 2022-10-12実行
   * @param keishaArray
   */
  async updateDeletedKeishaAlert(keishaArray: Keisha[]) {
    const deletedKeishaIDs = keishaArray.filter(keisha => keisha.isDeleted).map(keisha => keisha.id);
    const batch = this.db.firestore.batch();
    await this.setDefaultKakuninDateForDeletedKeishaAlerts(deletedKeishaIDs, batch);
    if (confirm('更新しますか?')) {
      batch.commit();
    }
  }

  /**
   * 指定された鶏舎に紐づく未確認アラートの確認日に「9999-12-31」を入れる
   * 画面右上のアラート通知などに表示されないようにするため
   * @param deletedKeishaIDs
   * @param batch
   * @private
   */
  private async setDefaultKakuninDateForDeletedKeishaAlerts(
    deletedKeishaIDs: string[], batch: firebase.firestore.WriteBatch) {
    for (const keishaId of deletedKeishaIDs) {
      console.log(keishaId);
      const snapshot = await firstValueFrom(this.genAlertQuery({keishaId, kakuninDate: ''}).get());
      await snapshot.forEach(doc => {
        console.log(doc.data());
        batch.update(doc.ref, {kakuninDate: DEFAULT_JISSHIDATE})
      });
    }
  }
}
